import React, { useEffect, useMemo, useState } from 'react';

import CasinoCRMApi from '../../../../../casino/Api/CRM/CasinoCRMApi';
import Loader from '../../../../elements/Loader';
import { API_CASINO_SETTINGS_GAMES_SEO_DELETE } from '../../Helpers/CrmApiUrls';
import { CRM_PERMISSION } from '../../Helpers/CrmPermissions';

import { CASINO_GAME_CREATORS_SEO_CREATE, CASINO_GAME_CREATORS_SEO_EDIT } from '../../Helpers/CrmUrls';

import { replaceUrlParams } from '../../Helpers/UrlHelper';

import ActionButton from 'crm/elements/ActionButton';
import ButtonCallback from 'crm/elements/ButtonCallback';
import DeleteButton from 'crm/elements/DeleteButton';
import EditButton from 'crm/elements/EditButton';
import Table from 'crm/elements/Table';
import SweetAlert from 'crm/elements/alerts/SweetAlert';
import useNumberParams from 'crm/hooks/useNumberParams';

export default function GameCreatorSeoList() {
    const { id } = useNumberParams();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [translations, setTranslations] = useState({});

    const loadState = async (): Promise<void> => {
        const translationsFromApi = await CasinoCRMApi.seoSettingsApi.getAll(id);
        setTranslations(translationsFromApi);
    };

    useEffect((): void => {
        setIsLoading(true);
        loadState();
        setIsLoading(false);
    }, []);

    const columns = useMemo(
        () => [
            {
                Header: 'Template number',
                accessor: (row, index) => index + 1,
            },
            {
                Header: 'Language',
                accessor: 'language.name',
            },
            {
                Header: 'Title',
                accessor: 'title',
            },
            {
                Header: 'Description',
                accessor: 'description',
            },
            {
                Header: 'Actions',
                accessor: 'id',
                Cell: ({ value }) => (
                    <div>
                        <EditButton
                            url={replaceUrlParams(CASINO_GAME_CREATORS_SEO_EDIT, { id: value })}
                            permissions={[CRM_PERMISSION.EDIT_CASINO_GAME_CREATOR_SEO]}
                        />
                        <DeleteButton
                            url={replaceUrlParams(API_CASINO_SETTINGS_GAMES_SEO_DELETE, { id: value })}
                            permissions={[CRM_PERMISSION.DELETE_CASINO_GAME_CREATOR_SEO]}
                            action={loadState}
                        />
                    </div>
                ),
            },
        ],
        []
    );

    const assignTemplatesToGames = () => {
        CasinoCRMApi.seoSettingsApi
            .assignTemplatesToGames(id)
            .then(() => {
                SweetAlert.success('Templates has been updated correctly').then(() => {
                    loadState();
                });
            })
            .catch((error) => {
                let message = 'Server error';
                if (!error.response) {
                    message = error.message;
                } else if (error.response.status !== 500) {
                    message = error.response.data.errors;
                }

                SweetAlert.error(message);
            });
    };

    const getContent = () => {
        return (
            <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <span>Game creator seo templates</span>
                    <div className="buttons">
                        <ActionButton
                            url={replaceUrlParams(CASINO_GAME_CREATORS_SEO_CREATE, { id })}
                            permissions={[CRM_PERMISSION.CREATE_CASINO_GAME_CREATOR_SEO]}
                            title="Create a new template"
                        />
                        <ButtonCallback
                            callback={assignTemplatesToGames}
                            title="Assign templates to games"
                            permissions={[CRM_PERMISSION.CREATE_CASINO_GAME_CREATOR_SEO]}
                        />
                    </div>
                </div>
                <div className="card-body">
                    {Object.keys(translations).length > 0 ? (
                        <Loader loading={isLoading} content={() => <Table columns={columns} data={translations} />} />
                    ) : (
                        <p>Cannot find any game creator seo template.</p>
                    )}
                </div>
            </div>
        );
    };

    return <Loader loading={isLoading} content={getContent} />;
}
